import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  CreateSmartphoneDto,
  SmartphoneListItemType,
  SmartphoneType,
  UpdateSmartphoneDto,
} from '../../types/smartphone/smartphone.type';
import { RouteParamsType } from '../../types/utils/router-params.type';
import { PaginationType } from '../../types/utils/pagination.type';
import { RootReducerState } from '../../redux/Reducers';
import axios from 'axios';
import { rejectWithApiError } from '../../utils/api';
import { setModalContent, setShowModal } from '../utils/utils.slice';
import { additionalQueryParams } from '../../utils/utilities';

export const getSmartphonesList = createAsyncThunk<
  PaginationType<SmartphoneListItemType>,
  RouteParamsType & { controller?: AbortController },
  { state: RootReducerState }
>('smartphones/getSmartphonesList/no-loader', async (data, thunkAPI) => {
  try {
    const { controller, ...params } = data;
    const additionalParams = additionalQueryParams(
      thunkAPI.getState().sharedReducer.selectedCompany,
      thunkAPI.getState().sharedReducer.selectedLocation,
      thunkAPI.getState().sharedReducer.includeSuborgs
    );
    const mergedParams = { ...params, ...additionalParams };

    const response = await axios.get<PaginationType<SmartphoneListItemType>>(
      `${process.env.REACT_APP_API_URL}/smartphones`,
      controller ? { signal: controller.signal, params: mergedParams } : { params: mergedParams }
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching Smartphones!' });
  }
});

export const getSmartphoneDetails = createAsyncThunk<SmartphoneType, { id: number }, { state: RootReducerState }>(
  'smartphones/getSmartphoneDetails',
  async ({ id }, thunkAPI) => {
    try {
      const response = await axios.get<SmartphoneType>(`${process.env.REACT_APP_API_URL}/smartphones/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching Smartphone!' });
    }
  }
);

export const patchSmartphoneDetails = createAsyncThunk<
  { id: number },
  { id: number; data: UpdateSmartphoneDto },
  { state: RootReducerState }
>('smartphones/patchSmartphoneDetails', async ({ data, id }, thunkAPI) => {
  try {
    const response = await axios.patch<{ id: number }>(`${process.env.REACT_APP_API_URL}/smartphones/${id}`, data);
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating Smartphone!' });
  }
});

export const postSmartphoneOwnershipTransfer = createAsyncThunk<
  void,
  { id: number; data: { owner: { id: number } } },
  { state: RootReducerState; rejectValue: string }
>('smartphones/postSmartphoneOwnershipTransfer', async ({ id, data }, thunkAPI) => {
  try {
    if (!data.owner?.id) {
      return rejectWithApiError({ thunkAPI, error: null, fallback: 'Owner organization is required!' });
    }

    await axios.post(`${process.env.REACT_APP_API_URL}/smartphones/${id}/ownership-transfer`, data);
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during Smartphone ownership transfer!' });
  }
});

export const postSmartphoneDetails = createAsyncThunk<
  SmartphoneType,
  { data: CreateSmartphoneDto },
  { state: RootReducerState }
>('smartphones/postSmartphoneDetails', async ({ data }, thunkAPI) => {
  try {
    const response = await axios.post<SmartphoneType>(`${process.env.REACT_APP_API_URL}/smartphones`, data);
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during creating Smartphone!' });
  }
});

export const deleteSmartphoneDetails = createAsyncThunk<
  { fetchList: boolean },
  { id: number; fetchList: boolean },
  { state: RootReducerState }
>('smartphones/deleteSmartphoneDetails', async ({ id, fetchList }, thunkAPI) => {
  try {
    await axios.delete<{ success: boolean }>(`${process.env.REACT_APP_API_URL}/smartphones/${id}`);
    if (fetchList) {
      thunkAPI.dispatch(getSmartphonesList(thunkAPI.getState().smartphonesReducer.smartphonesListRouteParams));
    }
    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));

    return { fetchList };
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during removing Smartphone!' });
  }
});
